// Clinic Service
// Handles all clinic-related API calls and data management

import authService from './Auth.services.js';
import { handleApiRequest, buildQueryParams, createApiHeaders, makeApiRequest } from '../utils/apiResponseHandler.js';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://the-live-cure-be.vercel.app/api';

class ClinicService {
  // Get all clinics with optional filters
  async getAllClinics(filters = {}) {
    const token = authService.getToken();
    const queryParams = buildQueryParams(filters);
    const headers = createApiHeaders(token);

    const apiCall = () => makeApiRequest(`${API_BASE_URL}/clinics?${queryParams}`, {
      method: 'GET',
      headers
    });

    return handleApiRequest(apiCall, 'docs', 'fetch clinics');
  }

  // Get clinic by ID
  async getClinicById(clinicId) {
    try {
      const token = authService.getToken();
      
      const response = await fetch(`${API_BASE_URL}/clinics/${clinicId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch clinic');
      }

      const data = await response.json();
      
      // Handle different API response structures
      const clinic = data.clinic || data.result?.clinic || data.result || data;
      
      // Map API fields to component expectations
      return {
        ...clinic,
        // Ensure arrays are properly set
        specialties: Array.isArray(clinic.specialties) ? clinic.specialties : [],
        facilities: Array.isArray(clinic.facilities) ? clinic.facilities : [],
        amenities: Array.isArray(clinic.amenities) ? clinic.amenities : [],
        // Map workingHours if needed
        workingHours: clinic.workingHours || {},
        // Ensure other fields have fallbacks
        rating: clinic.rating || 0,
        reviews: clinic.reviews || 0,
        beds: clinic.beds || 0,
        doctors: clinic.doctors || 0,
        operatingRooms: clinic.operatingRooms || 0,
        icuBeds: clinic.icuBeds || 0,
      };
    } catch (error) {
      console.error('Get clinic by ID error:', error);
      throw error;
    }
  }

  // Create new clinic
  async createClinic(clinicData, imageFile = null) {
    try {
      const token = authService.getToken();
      
      let body;
      let headers = {};
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // If image is provided, use FormData; otherwise use JSON
      if (imageFile) {
        const formData = new FormData();
        formData.append('image', imageFile);
        
        // Append all other clinic data fields
        Object.keys(clinicData).forEach(key => {
          if (clinicData[key] !== null && clinicData[key] !== undefined) {
            if (Array.isArray(clinicData[key])) {
              // Handle arrays - append each item
              clinicData[key].forEach((item, index) => {
                formData.append(`${key}[]`, item);
              });
            } else if (typeof clinicData[key] === 'object') {
              // Handle objects like workingHours
              formData.append(key, JSON.stringify(clinicData[key]));
            } else {
              formData.append(key, clinicData[key]);
            }
          }
        });

        body = formData;
        // Don't set Content-Type header for FormData - browser will set it with boundary
      } else {
        headers['Content-Type'] = 'application/json';
        body = JSON.stringify(clinicData);
      }

      const response = await fetch(`${API_BASE_URL}/clinics`, {
        method: 'POST',
        headers,
        body,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to create clinic');
      }

      const data = await response.json();
      return data.clinic || data.result || data;
    } catch (error) {
      console.error('Create clinic error:', error);
      throw error;
    }
  }

  // Update clinic
  async updateClinic(clinicId, clinicData, imageFile = null) {
    try {
      const token = authService.getToken();
      
      let body;
      let headers = {};
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // If image is provided, use FormData; otherwise use JSON
      if (imageFile) {
        const formData = new FormData();
        formData.append('image', imageFile);
        
        // Append all other clinic data fields
        Object.keys(clinicData).forEach(key => {
          if (clinicData[key] !== null && clinicData[key] !== undefined) {
            if (Array.isArray(clinicData[key])) {
              // Handle arrays - append each item
              clinicData[key].forEach((item, index) => {
                formData.append(`${key}[]`, item);
              });
            } else if (typeof clinicData[key] === 'object') {
              // Handle objects like workingHours
              formData.append(key, JSON.stringify(clinicData[key]));
            } else {
              formData.append(key, clinicData[key]);
            }
          }
        });

        body = formData;
        // Don't set Content-Type header for FormData - browser will set it with boundary
      } else {
        headers['Content-Type'] = 'application/json';
        body = JSON.stringify(clinicData);
      }

      const response = await fetch(`${API_BASE_URL}/clinics/${clinicId}`, {
        method: 'PUT',
        headers,
        body,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to update clinic');
      }

      const data = await response.json();
      return data.clinic || data.result || data;
    } catch (error) {
      console.error('Update clinic error:', error);
      throw error;
    }
  }

  // Delete clinic
  async deleteClinic(clinicId) {
    try {
      const token = authService.getToken();
      
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/clinics/${clinicId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete clinic');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Delete clinic error:', error);
      throw error;
    }
  }

  // Get clinics by city
  async getClinicsByCity(city) {
    try {
      const token = authService.getToken();
      
      const response = await fetch(`${API_BASE_URL}/clinics/city/${city}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch clinics by city');
      }

      const data = await response.json();
      return data.clinics;
    } catch (error) {
      console.error('Get clinics by city error:', error);
      throw error;
    }
  }

  // Get clinics by type
  async getClinicsByType(type) {
    try {
      const token = authService.getToken();
      
      const response = await fetch(`${API_BASE_URL}/clinics/type/${type}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch clinics by type');
      }

      const data = await response.json();
      return data.clinics;
    } catch (error) {
      console.error('Get clinics by type error:', error);
      throw error;
    }
  }

  // Search clinics
  async searchClinics(searchTerm) {
    try {
      const token = authService.getToken();
      
      const response = await fetch(`${API_BASE_URL}/clinics/search?q=${encodeURIComponent(searchTerm)}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to search clinics');
      }

      const data = await response.json();
      return data.clinics;
    } catch (error) {
      console.error('Search clinics error:', error);
      throw error;
    }
  }

  // Get clinic statistics
  async getClinicStats() {
    try {
      const token = authService.getToken();
      
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/clinics/stats`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch clinic statistics');
      }

      const data = await response.json();
      return data.stats;
    } catch (error) {
      console.error('Get clinic stats error:', error);
      throw error;
    }
  }

  // Upload clinic image
  async uploadClinicImage(clinicId, imageFile) {
    try {
      const token = authService.getToken();
      
      if (!token) {
        throw new Error('No authentication token');
      }

      const formData = new FormData();
      formData.append('image', imageFile);

      const response = await fetch(`${API_BASE_URL}/clinics/${clinicId}/image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to upload clinic image');
      }

      const data = await response.json();
      return data.imageUrl;
    } catch (error) {
      console.error('Upload clinic image error:', error);
      throw error;
    }
  }

  // Get clinic doctors
  async getClinicDoctors(clinicId) {
    try {
      const token = authService.getToken();
      
      const response = await fetch(`${API_BASE_URL}/clinics/${clinicId}/doctors`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch clinic doctors');
      }

      const data = await response.json();
      return data.doctors;
    } catch (error) {
      console.error('Get clinic doctors error:', error);
      throw error;
    }
  }

  // Add doctor to clinic
  async addDoctorToClinic(clinicId, doctorId) {
    try {
      const token = authService.getToken();
      
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/clinics/${clinicId}/doctors`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ doctorId }),
      });

      if (!response.ok) {
        throw new Error('Failed to add doctor to clinic');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Add doctor to clinic error:', error);
      throw error;
    }
  }

  // Remove doctor from clinic
  async removeDoctorFromClinic(clinicId, doctorId) {
    try {
      const token = authService.getToken();
      
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/clinics/${clinicId}/doctors/${doctorId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to remove doctor from clinic');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Remove doctor from clinic error:', error);
      throw error;
    }
  }

  // Get clinic working hours
  async getClinicWorkingHours(clinicId) {
    try {
      const token = authService.getToken();
      
      const response = await fetch(`${API_BASE_URL}/clinics/${clinicId}/working-hours`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch clinic working hours');
      }

      const data = await response.json();
      return data.workingHours;
    } catch (error) {
      console.error('Get clinic working hours error:', error);
      throw error;
    }
  }

  // Update clinic working hours
  async updateClinicWorkingHours(clinicId, workingHoursData) {
    try {
      const token = authService.getToken();
      
      const headers = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/clinics/${clinicId}/working-hours`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(workingHoursData),
      });

      if (!response.ok) {
        throw new Error('Failed to update clinic working hours');
      }

      const data = await response.json();
      return data.workingHours;
    } catch (error) {
      console.error('Update clinic working hours error:', error);
      throw error;
    }
  }

  // Get clinic reviews
  async getClinicReviews(clinicId) {
    try {
      const token = authService.getToken();
      
      const response = await fetch(`${API_BASE_URL}/clinics/${clinicId}/reviews`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch clinic reviews');
      }

      const data = await response.json();
      return data.reviews;
    } catch (error) {
      console.error('Get clinic reviews error:', error);
      throw error;
    }
  }

  // Add clinic review
  async addClinicReview(clinicId, reviewData) {
    try {
      const token = authService.getToken();
      
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/clinics/${clinicId}/reviews`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(reviewData),
      });

      if (!response.ok) {
        throw new Error('Failed to add clinic review');
      }

      const data = await response.json();
      return data.review;
    } catch (error) {
      console.error('Add clinic review error:', error);
      throw error;
    }
  }

  // Get clinic types
  async getClinicTypes() {
    try {
      const token = authService.getToken();
      
      const response = await fetch(`${API_BASE_URL}/clinics/types`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch clinic types');
      }

      const data = await response.json();
      return data.types;
    } catch (error) {
      console.error('Get clinic types error:', error);
      throw error;
    }
  }

  // Get clinic cities
  async getClinicCities() {
    try {
      const token = authService.getToken();
      
      const response = await fetch(`${API_BASE_URL}/clinics/cities`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch clinic cities');
      }

      const data = await response.json();
      return data.result?.cities || data.cities;
    } catch (error) {
      console.error('Get clinic cities error:', error);
      throw error;
    }
  }

  // Get clinic statistics
  async getClinicStats() {
    try {
      const token = authService.getToken();
      
      const response = await fetch(`${API_BASE_URL}/clinics/stats`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch clinic stats');
      }

      const data = await response.json();
      return data.result || data.stats;
    } catch (error) {
      console.error('Get clinic stats error:', error);
      throw error;
    }
  }

  // Upload clinic image
  async uploadClinicImage(clinicId, imageFile) {
    try {
      const token = authService.getToken();
      
      if (!token) {
        throw new Error('No authentication token');
      }

      const formData = new FormData();
      formData.append('image', imageFile);

      const response = await fetch(`${API_BASE_URL}/clinics/${clinicId}/image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to upload clinic image');
      }

      const data = await response.json();
      return data.result || data;
    } catch (error) {
      console.error('Upload clinic image error:', error);
      throw error;
    }
  }

  // Get clinic doctors
  async getClinicDoctors(clinicId) {
    try {
      const token = authService.getToken();
      
      const response = await fetch(`${API_BASE_URL}/clinics/${clinicId}/doctors`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch clinic doctors');
      }

      const data = await response.json();
      return data.result || data;
    } catch (error) {
      console.error('Get clinic doctors error:', error);
      throw error;
    }
  }

  // Add doctor to clinic
  async addDoctorToClinic(clinicId, doctorId) {
    try {
      const token = authService.getToken();
      
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/clinics/${clinicId}/doctors`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ doctorId }),
      });

      if (!response.ok) {
        throw new Error('Failed to add doctor to clinic');
      }

      const data = await response.json();
      return data.result || data;
    } catch (error) {
      console.error('Add doctor to clinic error:', error);
      throw error;
    }
  }

  // Remove doctor from clinic
  async removeDoctorFromClinic(clinicId, doctorId) {
    try {
      const token = authService.getToken();
      
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/clinics/${clinicId}/doctors/${doctorId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to remove doctor from clinic');
      }

      const data = await response.json();
      return data.result || data;
    } catch (error) {
      console.error('Remove doctor from clinic error:', error);
      throw error;
    }
  }
}

// Create and export a singleton instance
const clinicService = new ClinicService();
export default clinicService;
